// Fixed-width bitset over the fact universe (≤ 2·12² = 288 bits → 9 Uint32 words).
//
// This is the hot data structure for the whole analysis layer: the trace simulator tests
// "are this support's premises satisfied?" once per live support per step, which is a subset
// test — 9 AND-compares here instead of re-running the rule engine.

export class BitSet {
  readonly words: Uint32Array;

  constructor(bits: number, words?: Uint32Array) {
    this.words = words ?? new Uint32Array(Math.ceil(bits / 32));
  }

  static of(bits: number, members: Iterable<number>): BitSet {
    const s = new BitSet(bits);
    for (const m of members) s.add(m);
    return s;
  }

  add(i: number): void {
    this.words[i >>> 5] |= 1 << (i & 31);
  }

  has(i: number): boolean {
    return (this.words[i >>> 5] & (1 << (i & 31))) !== 0;
  }

  addAll(other: BitSet): void {
    for (let i = 0; i < this.words.length; i++) this.words[i] |= other.words[i];
  }

  /** Every bit of `this` is also set in `other`. The simulator's liveness test. */
  isSubsetOf(other: BitSet): boolean {
    for (let i = 0; i < this.words.length; i++) {
      if ((this.words[i] & ~other.words[i]) !== 0) return false;
    }
    return true;
  }

  intersects(other: BitSet): boolean {
    for (let i = 0; i < this.words.length; i++) {
      if ((this.words[i] & other.words[i]) !== 0) return true;
    }
    return false;
  }

  get size(): number {
    let n = 0;
    for (let i = 0; i < this.words.length; i++) {
      let w = this.words[i];
      while (w) { w &= w - 1; n++; }
    }
    return n;
  }

  clone(): BitSet {
    return new BitSet(0, new Uint32Array(this.words));
  }

  *[Symbol.iterator](): Generator<number> {
    for (let i = 0; i < this.words.length; i++) {
      let w = this.words[i];
      while (w) {
        const bit = w & -w;
        yield (i << 5) + Math.log2(bit >>> 0);
        w ^= bit;
      }
    }
  }

  toArray(): number[] {
    return [...this];
  }
}
